export enum FormType {
    RELEASE_OF_INFORMATION = "release_of_information",
}

export enum FormStatus {
    NOT_SENT = "not_sent",
    SENT = "sent",
    RECEIVED = "received",
    NEEDS_CORRECTION = "needs_correction",
    PAUSED = "paused",
    CANCELLED = "cancelled",
    COMPLETED = "completed",
    NEEDS_RENEWAL = "needs_renewal",
    RECEIVED_RENEWAL = "received_renewal",
    RENEWAL_SENT = "renewal_sent",
    NO_LONGER_NEEDED = "no_longer_needed",
    FOLLOW_UPS_COMPLETED = "follow_ups_completed",
}

export enum FormSource {
    INTAKE = "intake",
    MANTLE = "mantle",
}

export interface IFormDocument {
    document_id: string;
    document_url: string;
    document_name: string;
    uploaded_by: string;
    content_type: string;
    uploaded_at: string;
    document_type?: "generated_consent" | "form_attachment";
    field_id?: string;
    size?: number;
    page_count?: number;
    sha256?: string;
    page_audit?: IConsentDocumentPageAudit[];
}

export interface IConsentDocumentPageAudit {
    page_number: number;
    page_type: "consent" | "payment_card_authorization";
    timestamp: string;
    ip_address: string;
    user_agent: string;
    tracking_method: "direct" | "inferred" | "submission";
}

export interface IFormActivity {
    activity_id: string;
    timestamp: string;
    user_id: string;
    user_name: string;
    activity_type: string;
    description: string;
    metadata?: Record<string, unknown>;
}

export interface IClientForm {
    form_id: string;
    client_id: string;
    session_id: string;
    practice_id: string;
    form_type: string;
    form_source: FormSource;
    status: FormStatus;
    start_date: string;
    end_date: string;
    form_content: Record<string, unknown>;
    document_links: IFormDocument[];
    activity_log: IFormActivity[];
    signed_at: string;
    client_submitted_at: string;
    created_by: string;
    updated_by: string;
    created_at: string;
    updated_at: string;
    client_first_name: string;
    client_last_name: string;
    client_email: string;
}

export const PORTAL_SUPPORTED_FORM_TYPES = new Set([
    "humantold_consent_form",
    "chp_extern_therapy_consent_form",
    "chp_testing_consent_form",
    "ph_in_network_consent_form",
    // "chp_psych_appt_consent_form",
    // "chp_nutrition_counseling_consent_form",
    // "chp_career_assessment_consent_form",
    // "kap_consent",
    // "ph_nutrition_counseling_consent_form",
]);

export interface IConsentFormRecord {
    consent_id: string;
    client_id: string;
    session_id: string;
    provider_id: string;
    practice_id: string;
    status: FormStatus;
    start_date: string;
    signed_date: string;
    consent_documents: IFormDocument[];
    consent_form_sent_at: string;
    consent_form_link: string;
    consent_form_type: string | null;
    form_definition?: ConsentFormConfig | null;
    portal_supported: boolean;
    opened_at: string | null;
    draft_data: IConsentDraftData | null;
    client_submitted: boolean;
    client_submitted_at: string;
    session_details: {
        start_date: string;
        service: string;
        delivery_option: string;
        recurrence_rule: string;
        booking_id: string;
    };
    provider_details: {
        provider_id: string;
        first_name: string;
        last_name: string;
        credential: string;
        email: string;
    };
    client_details: {
        first_name: string;
        last_name: string;
        date_of_birth: string | null;
    } | null;
    ketamine_evaluator_details: {
        provider_id: string;
        first_name: string;
        last_name: string;
        credential: string | null;
        email: string;
    } | null;
    created_by: string;
    created_at: string;
    attendee_consents: IAttendeeConsentFormRecord[];
}

export interface IAttendeeConsentFormRecord extends Omit<
    IConsentFormRecord,
    "attendee_consents"
> {
    attendee_details: {
        session_attendee_id: string;
        first_name: string | null;
        last_name: string | null;
        email: string | null;
        date_of_birth: string | null;
        relationship: string | null;
    };
}

export interface IConsentFormsResponse {
    message: string;
    data: IConsentFormRecord[];
    count: number;
    total_count: number;
    current_page: number;
    total_pages: number;
}

export interface IClientFormFilters {
    search_string?: string;
    form_type?: FormType;
    status?: FormStatus;
    start_date_from?: string;
    start_date_to?: string;
    page?: number;
    limit?: number;
}

export interface IConsentFormFilters {
    page?: number;
    limit?: number;
}

export interface MultiCheckboxOption {
    key: string;
    label: string;
}

export interface ConsentTextFieldConfig {
    key: string;
    label: string;
    required?: boolean;
}

export interface ConsentContentNode {
    type: string;
    text?: string;
    className?: string;
    href?: string;
    children?: ConsentContentNode[];
}

export interface ConsentPageConfig {
    content: ConsentContentNode[];
    requiresInitials?: boolean;
    initialsFieldKey?: string;
    initialsLabel?: string;
    additionalInitialsFields?: ConsentTextFieldConfig[];
    requireFullName?: boolean;
    fullNameFieldKey?: string;
    fullNameLabel?: string;
    requiresCustodianFullName?: boolean;
    custodianFullNameFieldKey?: string;
    custodianFullNameLabel?: string;
    requiresCustodianDateOfBirth?: boolean;
    custodianDateOfBirthFieldKey?: string;
    custodianDateOfBirthLabel?: string;
    requiresDate?: boolean;
    dateFieldKey?: string;
    requiresDateOfBirth?: boolean;
    dateOfBirthFieldKey?: string;
    requiresSignature?: boolean;
    signatureFieldKey?: string;
    signatureLabel?: string;
    additionalSignatureFields?: ConsentTextFieldConfig[];
    additionalFullNameFields?: ConsentTextFieldConfig[];
    requiresCheckbox?: boolean;
    checkboxFieldKey?: string;
    checkboxLabel?: string;
    requiresMultiCheckbox?: boolean;
    multiCheckboxOptions?: MultiCheckboxOption[];
}

export interface ConsentPaymentCardStepConfig {
    acknowledgementText: string;
    initialsLabel?: string;
    additionalInitialsFields?: ConsentTextFieldConfig[];
    insertAfterPageIndex?: number;
}

export interface ConsentAttachmentFieldConfig {
    fieldId: string;
    label: string;
    description: string;
    required: boolean;
    maxFiles?: number;
}

export interface ConsentAttachmentsStepConfig {
    fields: ConsentAttachmentFieldConfig[];
}

export interface ConsentFormConfig {
    label: string;
    brand: string;
    variant?: "adult" | "minor";
    pages: ConsentPageConfig[];
    paymentCardStep?: ConsentPaymentCardStepConfig;
    attachmentsStep?: ConsentAttachmentsStepConfig;
}

export interface IConsentPaymentCardAuthorization {
    initials: string;
    additional_initials?: Record<string, string>;
}

export interface IConsentDraftData {
    current_step: number;
    completed_page_number?: number;
    completed_page_type?: "consent" | "payment_card_authorization";
    values: {
        page_initials?: Record<string, string>;
        page_full_names?: Record<string, string>;
        page_custodian_full_names?: Record<string, string>;
        page_custodian_dates_of_birth?: Record<string, string>;
        page_checkboxes?: Record<string, boolean>;
        page_multi_checkboxes?: Record<string, boolean>;
        page_dates?: Record<string, string>;
        page_date_of_births?: Record<string, string>;
        page_signatures?: Record<string, string>;
        payment_card_authorization?: IConsentPaymentCardAuthorization;
        [key: string]: unknown;
    };
    updated_at?: string;
}

export type IConsentDraftPayload = Omit<IConsentDraftData, "updated_at">;

export type ConsentActionType =
    "client_opened" | "client_submitted" | "client_downloaded";

export interface IConsentFormAnswers {
    page_initials: Record<string, string>;
    page_full_names: Record<string, string>;
    page_custodian_full_names?: Record<string, string>;
    page_custodian_dates_of_birth?: Record<string, string>;
    page_checkboxes: Record<string, boolean>;
    page_multi_checkboxes: Record<string, boolean>;
    page_dates: Record<string, string>;
    page_date_of_births: Record<string, string>;
    page_signatures: Record<string, string>;
    payment_card_authorization?: IConsentPaymentCardAuthorization;
}

export interface IConsentActionPayload {
    action_type: ConsentActionType;
    email_copy_requested?: boolean;
    form_answers?: IConsentFormAnswers;
}
